import CartEmpty from 'assets/svg/cart-icon/cart-empty.svg'
import CartIcon from 'assets/svg/cart-icon/cart-icon.svg'
import Badge from 'common/components/Badge'
import Button from 'common/components/Button'
import Popover from 'common/components/Popover'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import {
  ACTIVE_THEME_NAME,
  MEMBERSHIP_CHECKOUT_URL,
  MEMBERSHIP_LANDING_PAGE_URL,
  WEB_DESKTOP_RALALI_URL,
} from 'common/constants'
import theme from 'common/theme'
import eventTracking from 'lib/event-tracking'
import trackPosthog from 'lib/posthog/tracker'
import ThemeColorHelper from 'lib/theme-color-helper'
import { useCallback, useEffect } from 'react'
import Login from 'services/Auth/widgets/Login'
import V1GetCart from 'services/Cart/graphql/Queries/V1GetCart'
import CartItem from 'services/Cart/widgets/PopoverCart/CartItem'
import MembershipIllustration from 'services/Membership/assets/svg/membership-illustration.svg'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const CartContainer = styled.div`
  width: 396px;
`

const CartPopoverTitle = styled.div`
  padding: 12px 16px;
  box-shadow: 0 0 5px 0 rgba(134, 134, 134, 0.14);
`

const CartPopoverBody = styled.div`
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  max-height: 309px;
  overflow-y: auto;
`

const CartPopoverBodyMembership = styled.div`
  padding: 19px 17px;
  display: flex;
  flex-direction: column;
  max-height: 396px;
  overflow-y: auto;
`

const CartPopoverFooter = styled.div`
  border-top: 1px solid #f3f3f3;
  padding: 12px;
`

const Link = styled.a`
  ${theme.typography.bparagraph}
  color: ${theme.colors.orange};
  display: block;
  text-decoration: none;
  text-align: center;
`

const WrapperCartIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  color: ${() =>
    ACTIVE_THEME_NAME === 'default'
      ? theme.colors.grayDark
      : theme.colors.custom.white};
`

const WrapperMembershipIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 34px;
  height: 80px;
`

const StyledCartEmpty = styled(CartEmpty)`
  align-self: center;
`

interface ICart {
  isLogin?: boolean
  onOpenOverlay?: Function
  onCloseOverlay?: Function
}

function Cart({
  isLogin = false,
  onOpenOverlay,
  onCloseOverlay,
}: ICart): JSX.Element {
  const { cart, profile, permission } = useStoreState(
    (state) => state,
  )
  const { getCart } = useStoreActions((actions) => actions.cart)
  const { getPermissionList } = useStoreActions(
    (actions) => actions.permission,
  )
  const GetCartQuery = useCallback(() => {
    return V1GetCart(profile.profileData.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getPermissionList({
      page_url: 'https://www.ralali.com',
      actions: ['website.cart_access'],
    })

    if (isLogin && profile.profileData?.id) {
      getCart({
        query: GetCartQuery(),
      })
    }
  }, [
    GetCartQuery,
    getPermissionList,
    getCart,
    isLogin,
    profile.profileData.id,
  ])

  const posthog = eventTracking(trackPosthog())

  const trackViewCart = useCallback(() => {
    posthog.track('Clicked', {
      Name: 'View Cart',
      'Cart ID': cart.data?.id,
    })
  }, [posthog, cart.data])

  function CartBody() {
    if (!isLogin) {
      return (
        <>
          <StyledCartEmpty />
          <Typography marginBottom={24} textAlign="center">
            Anda harus login terlebih dahulu
          </Typography>
          <Login>
            <Button
              fontWeight="bold"
              letterCase="capitalize"
              margin="0 0 20px 0"
              block
            >
              Masuk / Daftar
            </Button>
          </Login>
        </>
      )
    }

    if (isLogin && cart.isLoading) {
      return (
        <>
          <Skeleton height="20px" />
          <Skeleton height="20px" marginTop="8px" />
        </>
      )
    }

    if (isLogin && cart.isError) {
      return (
        <Typography
          textAlign="center"
          marginTop="12px"
          marginBottom="12px"
        >
          Terjadi Kesalahan
        </Typography>
      )
    }

    let max = 0
    if (isLogin && cart.data?.total_items > 0) {
      return (
        <>
          {cart.data?.detail?.map((vendor) => {
            return vendor.details?.map((item) => {
              max += 1
              if (max < 21) {
                return (
                  <CartItem
                    key={item.id_item}
                    item={item}
                    micrositeUrl={vendor.microsite_url}
                  />
                )
              }
              return null
            })
          })}
        </>
      )
    }

    return (
      <>
        <StyledCartEmpty />
        <Typography marginBottom={24} textAlign="center">
          Keranjang Kosong
        </Typography>
      </>
    )
  }

  function PopoverCartMembership(): JSX.Element {
    return (
      <CartContainer data-testid="cart-container">
        <CartPopoverTitle>
          <Typography type="bparagraph">Keranjang Belanja</Typography>
        </CartPopoverTitle>
        <CartPopoverBodyMembership>
          <WrapperMembershipIcon>
            <MembershipIllustration height="80" />
          </WrapperMembershipIcon>
          <Typography textAlign="center" type="rparagraph">
            Anda belum menjadi member. Buka akses eksklusif dengan
            bergabung member sekarang
          </Typography>
          <Link href={`${MEMBERSHIP_LANDING_PAGE_URL}`}>
            <Typography
              textAlign="center"
              type="bparagraph"
              color="orange"
            >
              Pelajari Lebih Lanjut
            </Typography>
          </Link>
        </CartPopoverBodyMembership>

        <CartPopoverFooter>
          <Link href={`${MEMBERSHIP_CHECKOUT_URL}`}>
            Gabung Sekarang
          </Link>
        </CartPopoverFooter>
      </CartContainer>
    )
  }

  function PopoverContent(): JSX.Element {
    return (
      <CartContainer data-testid="cart-container">
        <CartPopoverTitle>
          <Typography type="bparagraph">
            <>
              Keranjang Belanja
              {cart.data?.total_items > 0 &&
                ` (${cart.data.total_items})`}
            </>
          </Typography>
        </CartPopoverTitle>
        <CartPopoverBody>
          <CartBody />
        </CartPopoverBody>
        {isLogin && (
          <CartPopoverFooter>
            <Link
              href={`${WEB_DESKTOP_RALALI_URL}/show-cart`}
              onClick={trackViewCart}
            >
              Lihat Selengkapnya
            </Link>
          </CartPopoverFooter>
        )}
      </CartContainer>
    )
  }

  return (
    <Popover
      side="bottom"
      full
      onOpenOverlay={onOpenOverlay}
      onCloseOverlay={onCloseOverlay}
      content={
        permission.permissionList['website.cart_access'] === 'no' ? (
          <PopoverCartMembership />
        ) : (
          <PopoverContent />
        )
      }
      dataTestId="popover-cart"
      childrenColor={ThemeColorHelper('orange')}
    >
      <WrapperCartIcon
        href={`${WEB_DESKTOP_RALALI_URL}/show-cart`}
        onClick={trackViewCart}
      >
        <CartIcon />
        {isLogin && cart.data?.total_items > 0 && (
          <Badge
            content={cart.data.total_items}
            top="0px"
            right="-4px"
            backgroundColor={theme.colors.red}
          />
        )}
      </WrapperCartIcon>
    </Popover>
  )
}

export default Cart
